import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import {
  MenuAvailabilityItem,
  SnoozeDuration,
  useMenuAvailabilityQuery,
} from '../../hooks/useMenuAvailabilityQuery';
import {
  Button,
  Callout,
  EmptyState,
  IconBox,
  IconSearch,
  Input,
  ListPage,
  Reason,
  SaveBar,
  Select,
  StatusLabel,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TRGroup,
} from '../../ui/v2';
import MenuSnoozeDialog from './MenuSnoozeDialog';
import {
  type AvailabilityFilter,
  effectiveSnoozed,
  FILTER_OPTIONS,
  filterCategories,
  formatDateTime,
  formatPrice,
  getPresetEnd,
  type StagedChange,
  toDateTimeLocal,
} from './menuAvailabilityModel';

const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  align?: 'left' | 'right';
}[] = [
  { key: 'item', label: 'Item' },
  { key: 'price', label: 'Price', width: 96, align: 'right' },
  { key: 'state', label: 'Current state', width: 176 },
  { key: 'expiry', label: 'Expiry', width: 216 },
  { key: 'action', label: 'Action', width: 136, align: 'right' },
];

function useUnsavedChangesWarning(hasChanges: boolean) {
  const blocker = useBlocker(hasChanges);
  const message =
    'You have unsaved menu availability changes. If you leave this page, those changes will be lost. Leave anyway?';

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm(message)) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, message]);

  useEffect(() => {
    if (!hasChanges) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges]);
}

export default function InventoryTable() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { queryKey, query, saveMutation } = useMenuAvailabilityQuery();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AvailabilityFilter>('all');
  const [changes, setChanges] = useState<Record<number, StagedChange>>({});
  const [modalItem, setModalItem] = useState<MenuAvailabilityItem | null>(null);
  const [duration, setDuration] = useState<SnoozeDuration>('until_close');
  const [customDateTime, setCustomDateTime] = useState('');
  const [modalError, setModalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const changeCount = Object.keys(changes).length;
  const canManage =
    query.data?.can_manage ??
    (user?.role === 'Owner' || user?.role === 'Manager');

  useUnsavedChangesWarning(changeCount > 0);

  useEffect(() => {
    setChanges({});
    setSuccessMessage('');
  }, [queryKey.join(':')]);

  const categories = useMemo(
    () =>
      filterCategories(query.data?.categories ?? [], changes, search, filter),
    [changes, filter, query.data?.categories, search],
  );

  const totalItems =
    query.data?.categories.reduce(
      (count, category) => count + category.items.length,
      0,
    ) ?? 0;

  const openSnoozeModal = (item: MenuAvailabilityItem) => {
    setModalItem(item);
    setDuration('until_close');
    setCustomDateTime(toDateTimeLocal(new Date(Date.now() + 60 * 60_000)));
    setModalError('');
  };

  const stageSnooze = () => {
    if (!modalItem) return;
    const endTime = getPresetEnd(
      duration,
      query.data?.next_closing_time ?? null,
      customDateTime,
    );
    if (!endTime) {
      setModalError(
        duration === 'until_close'
          ? 'No future closing time is configured. Select another duration.'
          : 'Choose a custom end date and time.',
      );
      return;
    }
    if (new Date(endTime).getTime() <= Date.now()) {
      setModalError('The snooze end time must be in the future.');
      return;
    }
    setChanges((current) => ({
      ...current,
      [modalItem.id]: {
        item_id: modalItem.id,
        action: 'snooze',
        duration,
        ...(duration === 'custom' ? { end_time: endTime } : {}),
        label: `Snoozed until ${formatDateTime(endTime)}`,
      },
    }));
    setModalItem(null);
    setSuccessMessage('');
  };

  const stageUnsnooze = (item: MenuAvailabilityItem) => {
    setChanges((current) => {
      if (!item.is_snoozed) {
        const next = { ...current };
        delete next[item.id];
        return next;
      }
      return {
        ...current,
        [item.id]: {
          item_id: item.id,
          action: 'unsnooze',
          label: 'Available after saving',
        },
      };
    });
    setSuccessMessage('');
  };

  const discardChanges = () => {
    if (
      changeCount > 0 &&
      !window.confirm('Discard all unsaved menu availability changes?')
    ) {
      return;
    }
    setChanges({});
    setSuccessMessage('');
  };

  const handleSave = async () => {
    if (!changeCount) return;
    setSuccessMessage('');
    try {
      await saveMutation.mutateAsync({
        changes: Object.values(changes).map(({ label: _label, ...change }) => ({
          ...change,
        })),
      });
      setChanges({});
      await queryClient.invalidateQueries({ queryKey });
      setSuccessMessage('Menu availability updated successfully.');
    } catch {
      // The mutation error remains visible and staged edits are intentionally retained.
    }
  };

  const loadError =
    query.error instanceof Error
      ? query.error.message
      : 'Failed to load menu availability.';
  const saveError =
    saveMutation.error instanceof Error
      ? saveMutation.error.message
      : 'Failed to save changes. Your edits are still staged.';
  const changeSummary = `${changeCount} unsaved ${
    changeCount === 1 ? 'change' : 'changes'
  }`;

  const renderItem = (item: MenuAvailabilityItem) => {
    const staged = changes[item.id];
    const isSnoozed = effectiveSnoozed(item, staged);
    const expiry =
      staged?.action === 'snooze'
        ? staged.label
        : item.snoozed_until
          ? `Until ${formatDateTime(item.snoozed_until)}`
          : '—';
    const providerManaged =
      item.is_snoozed && !item.can_unsnooze && staged?.action !== 'snooze';

    return (
      <TR key={item.id}>
        <TD className="overflow-hidden">
          <div className="flex min-w-0 items-baseline gap-2">
            <span className="truncate font-medium">{item.name}</span>
            {!item.base_available && (
              <span className="shrink-0 text-v2-12 text-v2-fg-3">
                Unavailable in source menu
              </span>
            )}
          </div>
        </TD>
        <TD num align="right" className="text-v2-fg-2">
          {formatPrice(item.price)}
        </TD>
        <TD>
          <div className="flex items-center gap-2">
            <StatusLabel tone={isSnoozed ? 'warning' : 'success'}>
              {isSnoozed ? 'Snoozed' : 'Available'}
            </StatusLabel>
            {staged && <Tag tone="accent">Unsaved</Tag>}
          </div>
        </TD>
        <TD num truncate className="text-v2-fg-3">
          {expiry}
        </TD>
        <TD align="right">
          {canManage ? (
            isSnoozed ? (
              <Button
                size="sm"
                onClick={() => stageUnsnooze(item)}
                disabledReason={
                  providerManaged
                    ? 'This snooze is managed by your POS provider.'
                    : undefined
                }
              >
                {providerManaged ? 'POS managed' : 'Unsnooze'}
              </Button>
            ) : (
              <Button size="sm" onClick={() => openSnoozeModal(item)}>
                Snooze
              </Button>
            )
          ) : (
            <span className="text-v2-12 text-v2-fg-4">View only</span>
          )}
        </TD>
      </TR>
    );
  };

  let body: React.ReactNode;
  if (query.isPending) {
    body = (
      <>
        <p role="status" className="sr-only">
          Loading menu availability…
        </p>
        <Table fixed className="min-w-[760px]">
          <TBody>
            <TableSkeletonRows cols={COLUMNS.length} />
          </TBody>
        </Table>
      </>
    );
  } else if (query.isError) {
    body = (
      <div className="p-4 lg:p-6">
        <Callout tone="danger" title="Couldn't load menu availability">
          <p>{loadError}</p>
          <Button
            size="sm"
            className="mt-2"
            onClick={() => {
              void query.refetch();
            }}
          >
            Try again
          </Button>
        </Callout>
      </div>
    );
  } else if (totalItems === 0) {
    body = (
      <EmptyState
        icon={<IconBox />}
        title="No menu items found"
        description="Active menu items will appear here after your menu is synced."
      />
    );
  } else if (categories.length === 0) {
    body = (
      <EmptyState
        icon={<IconSearch />}
        title="No items match your search and filter."
      />
    );
  } else {
    body = (
      <div className="v2-scroll overflow-x-auto">
        <Table fixed className="min-w-[760px]">
          <THead>
            <tr>
              {COLUMNS.map((column) => (
                <TH key={column.key} width={column.width} align={column.align}>
                  {column.label}
                </TH>
              ))}
            </tr>
          </THead>
          <TBody>
            {categories.map((category) => (
              <React.Fragment key={category.id}>
                <TRGroup
                  colSpan={COLUMNS.length}
                  label={category.name}
                  count={category.items.length}
                />
                {category.items.map(renderItem)}
              </React.Fragment>
            ))}
          </TBody>
        </Table>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title="AskArthur | Menu Availability"
        description="Manage temporary menu item availability"
      />

      <ListPage
        title="Menu availability"
        description="Temporarily snooze items without changing your source menu."
        actions={
          successMessage ? (
            <span role="status" className="text-v2-12 text-v2-success">
              {successMessage}
            </span>
          ) : null
        }
        toolbar={
          <div className="flex min-h-11 flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 sm:flex-nowrap sm:py-0 lg:px-6">
            <div className="w-full min-w-0 sm:w-[280px] sm:shrink">
              <Input
                type="search"
                size="sm"
                aria-label="Search items or categories"
                placeholder="Search items or categories"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                leading={<IconSearch size={14} />}
              />
            </div>
            <Select
              size="sm"
              aria-label="Filter availability"
              value={filter}
              onChange={(event) =>
                setFilter(event.target.value as AvailabilityFilter)
              }
              className="w-[148px] shrink-0"
            >
              {FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        }
      >
        {!canManage && (
          <Reason className="border-b border-v2-line px-4 py-2 lg:px-6">
            Staff accounts can view availability. An owner or manager must save
            changes.
          </Reason>
        )}

        {body}

        {canManage && (
          <>
            <p className="sr-only" aria-live="polite">
              {changeSummary}
            </p>
            <SaveBar
              dirty={changeCount > 0}
              saving={saveMutation.isPending}
              error={saveMutation.isError ? saveError : null}
              summary={changeSummary}
              onSave={() => {
                void handleSave();
              }}
              onDiscard={discardChanges}
            />
          </>
        )}
      </ListPage>

      <MenuSnoozeDialog
        item={modalItem}
        nextClosingTime={query.data?.next_closing_time ?? null}
        duration={duration}
        customDateTime={customDateTime}
        error={modalError}
        onDurationChange={(value) => {
          setDuration(value);
          setModalError('');
        }}
        onCustomDateTimeChange={(value) => {
          setCustomDateTime(value);
          setModalError('');
        }}
        onClose={() => setModalItem(null)}
        onStage={stageSnooze}
      />
    </>
  );
}
